import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {RequestInfo} from "@khan/shared-core";

import type {
    unlinkAuthEmailType,
    unlinkAuthEmail_unlinkEmail_error as UnlinkEmailError,
} from "./__graphql-types__/unlinkAuthEmail.ts";
import ConnectEmail from "./connect-email.tsx";
import {MaybePasswordRequiredMessage} from "./maybe-password-required-message.tsx";
import ResponsiveLinkedAccountRow from "./responsive-linked-account-row.tsx";
import {UNLINK_EMAIL_MUTATION} from "./unlink-email-mutation.ts";

import EnvelopeSimpleIcon from "@phosphor-icons/core/regular/envelope-simple.svg";
import minusCircleIcon from "@phosphor-icons/core/regular/minus-circle.svg";

type ErrorCode = UnlinkEmailError["code"];
type Data = unlinkAuthEmailType["response"];

const ERROR_CODE_MESSAGE_MAPPING: Partial<
    Record<ErrorCode, (arg1: string) => string>
> = {
    EMAIL_ISNT_LINKED_TO_ACCOUNT: (email: string) =>
        t`The email address ${email} isn't linked to your Khan Academy account`,
    MUST_SET_PRIMARY_EMAIL_BEFORE_REMOVING: (email: string) =>
        t`Please set your primary email address to be a different address before removing ${email}.`,
    CANNOT_REMOVE_PRIMARY_EMAIL: (email: string) =>
        t`Please set your primary email address to be a different address before removing ${email}.`,
};

const strings = {
    confirmDisconnectEmail: (email: string) =>
        t`Are you sure you want to remove ${email} from your account?`,

    noEmailModifyingChild: (age: number) => {
        const newEnString = `There is no email address connected to this Khan Academy account. Since your child is ${age} years old, your child's email address can be added in their settings page, so they can receive notifications.`;
        const newI18nString = t`There is no email address connected to this Khan Academy account. Since your child is ${age} years old, your child's email address can be added in their settings page, so they can receive notifications.`;
        if (RequestInfo.kaLocale === "en" || newEnString !== newI18nString) {
            return newI18nString;
        }
        // TODO(somewhatabstract, TR-1250): Remove this old string once all
        // locales have translations for the new string above. Check the
        // translations portal or with the translations team to be sure.
        return t`There is no email address connected to this Khan Academy account. Since your child is ${age} years old, your two accounts can be unlinked by adding an email address by going to this settings page from your child's account.`;
    },

    noEmailNotModifyingChild: (age: number, hasManagingParent: boolean) => {
        if (!hasManagingParent) {
            const newEnString = `You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account so you can receive email notifications.`;
            const newI18nString = t`You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account so you can receive email notifications.`;
            if (
                RequestInfo.kaLocale === "en" ||
                newEnString !== newI18nString
            ) {
                return newI18nString;
            }
        }

        const newEnString = `You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account so you can receive email notifications. Your linked parent will continue to be your coach unless you edit your list of coaches from your profile page.`;
        const newI18nString = t`You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account so you can receive email notifications. Your linked parent will continue to be your coach unless you edit your list of coaches from your profile page.`;
        if (RequestInfo.kaLocale === "en" || newEnString !== newI18nString) {
            return newI18nString;
        }
        // TODO(somewhatabstract, TR-1250): Remove this old string once all
        // locales have translations for the new string above. Check the
        // translations portal or with the translations team to be sure.
        return t`You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account, which will unlink it from your parent's account.`;
    },
};

interface Props {
    age: number | null | undefined;
    authEmails: Array<string> | null | undefined;
    /**
     * Whether the user has the ability to reauthenticate the account. This can
     * be either a password or a single-sign on method.
     */
    canReauth: boolean;
    hasManagingParent: boolean;
    isModifyingChild: boolean;
    identifier: string | null | undefined;
}

/**
 * Renders email content for the summary row, showing primary email and count of additional emails if applicable.
 */
const renderEmailContent = (
    primaryEmail: string | undefined,
    hasMultipleEmails: boolean,
    authEmails: Array<string>,
): React.ReactNode => {
    if (!primaryEmail) {
        return undefined;
    }

    if (hasMultipleEmails) {
        return (
            <>
                {primaryEmail}
                <span
                    style={{
                        color: semanticColor.core.foreground.neutral.subtle,
                    }}
                >
                    {` +${authEmails.length - 1} `}
                    {t`more`}
                </span>
            </>
        );
    }

    return primaryEmail;
};

/**
 * Renders the connect email action button based on user permissions and context.
 */
const renderEmailAction = (
    isModifyingChild: boolean,
    canReauth: boolean,
    authEmails: Array<string> | null | undefined,
    identifier: string | null | undefined,
): React.ReactNode => {
    /*
     * - Do not show the connect email action to parents modifying managed child
     *   accounts.
     * - Do not show the connect email action to users who cannot reauthenticate
     *   their account. This can be the case for a parent-managed child account.
     */
    if (isModifyingChild || !canReauth) {
        return null;
    }

    return (
        <ConnectEmail
            buttonText={
                authEmails?.length
                    ? t`Connect another email`
                    : t`Connect an email`
            }
            identifier={identifier}
        />
    );
};

/**
 * Renders the disconnect button for email items based on device type and context.
 */
const renderDisconnectButton = (
    emailsLength: number,
    isLoading: boolean,
    onClickDisconnect: () => void,
): React.ReactNode => {
    if (emailsLength <= 1) {
        return null;
    }

    if (RequestInfo.isPhone) {
        return (
            <IconButton
                actionType="destructive"
                icon={minusCircleIcon}
                aria-label={t`Disconnect`}
                onClick={onClickDisconnect}
            />
        );
    }

    return (
        <Button
            onClick={onClickDisconnect}
            kind="tertiary"
            size="small"
            spinner={isLoading}
            style={styles.disconnectButton}
        >
            {t`Disconnect`}
        </Button>
    );
};

export const LinkEmail: React.FC<Props> = ({
    age,
    authEmails,
    canReauth,
    hasManagingParent,
    isModifyingChild,
    identifier,
}) => {
    const [disconnectEmailSuccessMessage, setDisconnectEmailSuccessMessage] =
        React.useState("");
    const [unlinkEmailMutation, result] = useMutation(UNLINK_EMAIL_MUTATION);

    const handleMutationCompletion = (result: Data, email: string) => {
        if (result?.unlinkEmail && !result.unlinkEmail.error) {
            setDisconnectEmailSuccessMessage(
                t`Successfully removed ${email} from your Khan Academy account.`,
            );
        }
    };

    const renderEmailAndDisconnect = (email: string, emailsLength: number) => {
        let errorMessage: string | null = null;

        if (result?.status === "error") {
            errorMessage = t`Oops. Something went wrong. Please try again.`;
        }

        if (
            result?.status === "success" &&
            result.data.unlinkEmail?.error?.code
        ) {
            const errorFunction =
                ERROR_CODE_MESSAGE_MAPPING[result.data.unlinkEmail.error.code];
            errorMessage = errorFunction
                ? errorFunction(email)
                : t`Oops. Something went wrong. Please try again.`;
        }

        const handleOnClickDisconnect = async () => {
            // eslint-disable-next-line no-alert -- allow alerts
            if (!confirm(strings.confirmDisconnectEmail(email))) {
                return;
            }

            const mutationResult = await unlinkEmailMutation({
                authEmail: email,
            });
            if (mutationResult.status === "success") {
                handleMutationCompletion(mutationResult.data, email);
            }
        };

        return (
            <View key={email}>
                <View style={styles.emailRow}>
                    <View style={styles.emailTextContainer}>
                        <LabelMedium style={styles.emailText}>
                            {email}
                        </LabelMedium>
                    </View>
                    {renderDisconnectButton(
                        emailsLength,
                        result?.status === "loading",
                        handleOnClickDisconnect,
                    )}
                </View>
                {errorMessage && (
                    // TODO(hack25-darkmode): Change to Banner.critical
                    <Body style={styles.errorMessage}>{errorMessage}</Body>
                )}
            </View>
        );
    };

    const primaryEmail = authEmails?.[0];
    const hasMultipleEmails = authEmails && authEmails.length > 1;

    const emailContent = renderEmailContent(
        primaryEmail,
        !!hasMultipleEmails,
        authEmails || [],
    );

    const emailAction = renderEmailAction(
        isModifyingChild,
        canReauth,
        authEmails,
        identifier,
    );

    return (
        <>
            <ResponsiveLinkedAccountRow
                icon={
                    <PhosphorIcon
                        aria-hidden={true}
                        icon={EnvelopeSimpleIcon}
                        size="medium"
                        style={{height: 28, width: 28}}
                    />
                }
                label={t`Email`}
                content={emailContent}
                action={emailAction}
            />
            {/* Show informational message when no emails are connected */}
            {(!authEmails || authEmails.length === 0) && age && (
                <>
                    <View style={{height: sizing.size_120}} />
                    <Body>
                        {isModifyingChild
                            ? strings.noEmailModifyingChild(age)
                            : strings.noEmailNotModifyingChild(
                                  age,
                                  hasManagingParent,
                              )}
                    </Body>
                    {isModifyingChild && (
                        <Body>
                            {t`You will continue to be your child's coach unless your account is manually removed from the list of coaches.`}
                        </Body>
                    )}
                    {!isModifyingChild && hasManagingParent && (
                        <Body>
                            {t`Your parent will continue to be your coach unless you edit your list of coaches from your profile page.`}
                        </Body>
                    )}
                </>
            )}
            <MaybePasswordRequiredMessage
                canReauth={canReauth}
                isModifyingChild={isModifyingChild}
                primaryEmail={primaryEmail}
            />
            {/* Show all emails with disconnect buttons when there are multiple */}
            {hasMultipleEmails && (
                <>
                    <View style={{height: sizing.size_120}} />
                    <View style={styles.additionalEmailsContainer}>
                        {authEmails.map((email) =>
                            renderEmailAndDisconnect(email, authEmails.length),
                        )}
                    </View>
                </>
            )}
            {disconnectEmailSuccessMessage && (
                // TODO(hack25-darkmode): Change to Banner.success
                <>
                    <View style={{height: sizing.size_120}} />
                    <Body style={styles.successMessage}>
                        {disconnectEmailSuccessMessage}
                    </Body>
                </>
            )}
        </>
    );
};

const styles = StyleSheet.create({
    additionalEmailsContainer: {
        paddingInlineStart: "172px", // Icon width + spacing + label width (updated for 160px section)
    },
    errorMessage: {
        color: semanticColor.core.foreground.critical.default,
    },
    successMessage: {
        color: semanticColor.core.foreground.success.default,
    },
    disconnectButton: {
        height: sizing.size_320,
    },
    emailRow: {
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
    },
    emailTextContainer: {
        flex: 1,
        justifyContent: "flex-end",
        paddingInlineEnd: sizing.size_120,
    },
    emailText: {
        color: semanticColor.core.foreground.neutral.strong,
        textAlign: "end",
    },
});
export default LinkEmail;
